import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { FONTE_TIPO_LABELS, formatBRL, formatDate, type Fonte } from '@/pages/painel/lib/radarConstants';
import RadarFonteFormModal from './RadarFonteFormModal';
import RadarFonteDetailModal from './RadarFonteDetailModal';

export default function RadarFontes() {
  const { password } = useOutletContext<{ password: string }>();
  const [fontes, setFontes] = useState<Fonte[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Fonte | null>(null);
  const [detalhando, setDetalhando] = useState<Fonte | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'fontes', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { fontes?: Fonte[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setFontes(parsed?.fontes ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar fontes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const abrirNova = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const abrirEdicao = (f: Fonte) => {
    setEditing(f);
    setModalOpen(true);
  };

  const statusBadge = (status: string) =>
    status === 'ativo'
      ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-gray-100 text-gray-500 border-gray-200';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-gray-900 text-base">Fontes de inteligência</h3>
          <p className="text-sm text-gray-500">Catálogo de origens — de onde a informação veio.</p>
        </div>
        <button
          onClick={abrirNova}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Adicionar fonte
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {loading && fontes.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : fontes.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
            <i className="ri-rss-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500 mb-4">Nenhuma fonte registrada ainda.</p>
          <button
            onClick={abrirNova}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Adicionar fonte
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-3 font-semibold">Fonte</th>
                  <th className="px-4 py-3 font-semibold text-center">Oportunidades</th>
                  <th className="px-4 py-3 font-semibold text-center">Obras</th>
                  <th className="px-4 py-3 font-semibold text-center">Quentes</th>
                  <th className="px-4 py-3 font-semibold text-right">Valor das obras</th>
                  <th className="px-4 py-3 font-semibold text-right">Potencial HOP</th>
                  <th className="px-4 py-3 font-semibold">Última ocorrência</th>
                  <th className="px-4 py-3 font-semibold text-center">Status</th>
                  <th className="px-4 py-3 font-semibold text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {fontes.map((f) => (
                  <tr
                    key={f.id}
                    className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors cursor-pointer"
                    onClick={() => setDetalhando(f)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-lg shrink-0">
                          <i className="ri-rss-line"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{f.nome}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                              {FONTE_TIPO_LABELS[f.tipo] ?? f.tipo}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{f.qtd_oportunidades}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{f.qtd_obras}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={f.quentes > 0 ? 'font-semibold text-red-600' : 'text-gray-400'}>{f.quentes}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800 whitespace-nowrap">{formatBRL(f.valor_obras)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {f.valor_potencial_hop > 0 ? (
                        <span className="font-semibold text-gray-800">{formatBRL(f.valor_potencial_hop)}</span>
                      ) : (
                        <span className="text-gray-400">Não estimado</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(f.ultima_ocorrencia)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block text-[11px] font-semibold rounded-full px-2.5 py-0.5 border ${statusBadge(f.status)}`}>
                        {f.status === 'ativo' ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirEdicao(f);
                        }}
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#E8610A] hover:bg-[#E8610A]/10 rounded-lg cursor-pointer"
                        title="Editar fonte"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <RadarFonteFormModal
          fonte={editing}
          password={password}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            load();
          }}
        />
      )}

      {detalhando && (
        <RadarFonteDetailModal
          fonte={detalhando}
          password={password}
          onClose={() => setDetalhando(null)}
        />
      )}
    </div>
  );
}